/**
 * Performance metrics calculation
 * Calculates all trading performance metrics from trades and equity curve
 */

import type { Trade, EquityPoint, PerformanceMetrics, RollingMetrics, Timeframe } from '../core/types.js';
import { TimeframeSchema, timeframeToMs } from '../core/types.js';
import {
  SORTINO_CAP,
  ROLLING_SHARPE_WINDOW,
  PROFIT_FACTOR_CAP,
  SHARPE_RETURN_MISMATCH_LOSS_EPS,
  SHARPE_RETURN_MISMATCH_SHARPE_EPS,
  SLIPPAGE_UNCERTAINTY_BPS_PER_FILL,
} from '../core/constants.js';

/**
 * SHA-28: compute the +- intrabar-fill-uncertainty band, expressed as a
 * percentage of initial capital, from the notional value of every fill
 * (trade) in a run.
 *
 * This is REPORTING METADATA ONLY. It reflects that a real exchange fill can
 * plausibly deviate from the backtest's modelled fill price by roughly
 * `SLIPPAGE_UNCERTAINTY_BPS_PER_FILL` bps (empirical finding vs real Bybit
 * fills — see SHA-28 background). It must NEVER be added to, or used to
 * adjust, any point-estimate metric (totalReturnPercent, sharpeRatio, etc).
 * Callers display it alongside the point estimate, e.g. "+12.3% (+-1.8%)".
 *
 * Combination method: each fill's uncertainty is treated as an independent
 * random deviation. Combining N independent uncertainties via a linear sum
 * would overstate the aggregate uncertainty for runs with many trades (the
 * per-fill deviations partially cancel in aggregate). Root-sum-of-squares
 * (quadrature) is the standard error-propagation combination for independent
 * uncertainties and grows sublinearly (~sqrt(n)) — far more realistic for
 * large trade counts while still widening with trade count and notional size.
 *
 * @param notionals      Notional value (price x amount) of every fill (open + close trades)
 * @param initialCapital Starting capital — basis for the percentage conversion
 * @returns Non-negative percentage-point width of the +- band. 0 when there
 *          are no fills or initialCapital <= 0 (percentage is undefined).
 */
export function calculateSlippageUncertaintyBandPercent(
  notionals: number[],
  initialCapital: number,
): number {
  if (initialCapital <= 0 || notionals.length === 0) return 0;

  const sumSquares = notionals.reduce((sum, notional) => {
    const perFillUncertainty = notional * (SLIPPAGE_UNCERTAINTY_BPS_PER_FILL / 10_000);
    return sum + perFillUncertainty * perFillUncertainty;
  }, 0);

  const totalUncertainty = Math.sqrt(sumSquares);
  return (totalUncertainty / initialCapital) * 100;
}

/** Compute the notional (price x amount) of every trade — shared helper for the band. */
function tradeNotionals(trades: Trade[]): number[] {
  return trades.map((t) => t.price * t.amount);
}

/**
 * Check if a trade is a close trade (has PnL)
 */
function isCloseTrade(trade: Trade): boolean {
  return trade.action === 'CLOSE_LONG' || trade.action === 'CLOSE_SHORT';
}

/**
 * Calculate performance metrics from trades and equity curve
 * Note: Metrics are calculated from CLOSE trades only (where PnL is realized)
 * @param trades - Array of all trades (open and close)
 * @param equity - Array of equity points over time
 * @param initialCapital - Starting capital
 * @param timeframe - Required timeframe for annualization (e.g., '1h', '1d')
 * @returns Complete performance metrics
 */
export function calculateMetrics(
  trades: Trade[],
  equity: EquityPoint[],
  initialCapital: number,
  timeframe: string
): PerformanceMetrics {
  // Fail loudly on a missing/unknown timeframe even on the zero-trade early-return path.
  getAnnualizationFactor(timeframe);

  // Filter to only close trades for PnL calculations
  const closeTrades = trades.filter(isCloseTrade);

  // Handle edge case of no close trades
  if (closeTrades.length === 0) {
    return {
      totalReturn: 0,
      totalReturnPercent: 0,
      maxDrawdown: 0,
      maxDrawdownPercent: 0,
      sharpeRatio: 0,
      sortinoRatio: 0,
      calmarRatio: 0,
      cagrPercent: 0,
      winRate: 0,
      profitFactor: 0,
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      avgWin: 0,
      avgLoss: 0,
      avgWinPercent: 0,
      avgLossPercent: 0,
      expectancy: 0,
      expectancyPercent: 0,
      largestWin: 0,
      largestLoss: 0,
      avgTradeDuration: 0,
      exposureTime: 0,
      totalFees: 0,
      totalSlippage: 0,
      // SHA-28: still reflect any OPEN-only fills (e.g. a position opened but
      // never closed) so the band is consistent with `trades`, not `closeTrades`.
      slippageUncertaintyBandPercent: calculateSlippageUncertaintyBandPercent(
        tradeNotionals(trades),
        initialCapital,
      ),
      longPnl: 0,
      shortPnl: 0,
      longTrades: 0,
      shortTrades: 0,
      longWinRate: 0,
      shortWinRate: 0,
      sharpeReturnSignMismatch: false,
    };
  }

  // Separate winning and losing trades (based on realized PnL)
  const winningTrades = closeTrades.filter((t) => (t.pnl ?? 0) > 0);
  const losingTrades = closeTrades.filter((t) => (t.pnl ?? 0) < 0);

  // Basic trade statistics
  const totalTrades = closeTrades.length;
  const winCount = winningTrades.length;
  const lossCount = losingTrades.length;

  // Win rate
  const winRate = (winCount / totalTrades) * 100;

  // Total return
  const finalEquity = equity.length > 0 ? equity[equity.length - 1].equity : initialCapital;
  const totalReturn = finalEquity - initialCapital;
  const totalReturnPercent = (totalReturn / initialCapital) * 100;

  // Gross profit and loss
  const grossProfit = winningTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0);
  const grossLoss = Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0));

  // Profit factor — cap at PROFIT_FACTOR_CAP to prevent Infinity (JSON.stringify(Infinity) === "null")
  const profitFactor = grossLoss > 0
    ? Math.min(grossProfit / grossLoss, PROFIT_FACTOR_CAP)
    : grossProfit > 0 ? PROFIT_FACTOR_CAP : 0;

  // Average win/loss
  const avgWin = winCount > 0 ? grossProfit / winCount : 0;
  const avgLoss = lossCount > 0 ? grossLoss / lossCount : 0;

  // Average win/loss percentages
  const avgWinPercent = winCount > 0
    ? winningTrades.reduce((sum, t) => sum + (t.pnlPercent ?? 0), 0) / winCount
    : 0;
  const avgLossPercent = lossCount > 0
    ? losingTrades.reduce((sum, t) => sum + (t.pnlPercent ?? 0), 0) / lossCount
    : 0;

  // Expectancy (expected value per trade)
  const expectancy = totalTrades > 0 ? totalReturn / totalTrades : 0;
  const expectancyPercent = totalTrades > 0
    ? closeTrades.reduce((sum, t) => sum + (t.pnlPercent ?? 0), 0) / totalTrades
    : 0;

  // Largest win/loss — use reduce instead of spread to avoid RangeError on large arrays
  const largestWin = winCount > 0
    ? winningTrades.reduce((m, t) => Math.max(m, t.pnl ?? 0), 0)
    : 0;
  const largestLoss = lossCount > 0
    ? losingTrades.reduce((m, t) => Math.min(m, t.pnl ?? 0), 0)
    : 0;

  // Average trade duration (from open trades to close trades)
  const avgTradeDuration = calculateAvgTradeDuration(trades);

  // Risk metrics — delegate to shared helper so paper-trading path stays in parity
  const {
    maxDrawdown,
    maxDrawdownPercent,
    sharpeRatio,
    sortinoRatio,
    cagrPercent,
    calmarRatio,
  } = calculateEquityRiskMetrics(equity, initialCapital, timeframe);

  // Sign-divergence flag: true when Sharpe and compound return disagree in sign with
  // non-trivial magnitudes (volatility-drag artifact of arithmetic per-bar Sharpe).
  // Positive Sharpe + meaningful net loss → flag; negative Sharpe + meaningful net gain → flag.
  const sharpeReturnSignMismatch =
    (sharpeRatio > SHARPE_RETURN_MISMATCH_SHARPE_EPS && totalReturnPercent < -SHARPE_RETURN_MISMATCH_LOSS_EPS) ||
    (sharpeRatio < -SHARPE_RETURN_MISMATCH_SHARPE_EPS && totalReturnPercent > SHARPE_RETURN_MISMATCH_LOSS_EPS);

  // Exposure time (percentage of time in the market)
  const exposureTime = calculateExposureTime(trades, equity);

  // Total fees paid across all trades
  const totalFees = trades.reduce((sum, t) => sum + (t.fee ?? 0), 0);

  // Total slippage cost across all trades
  const totalSlippage = trades.reduce((sum, t) => sum + (t.slippage ?? 0), 0);

  // SHA-28: +- intrabar-fill-uncertainty band (reporting metadata only — see
  // calculateSlippageUncertaintyBandPercent doc comment). Does NOT change any
  // point-estimate metric above.
  const slippageUncertaintyBandPercent = calculateSlippageUncertaintyBandPercent(
    tradeNotionals(trades),
    initialCapital,
  );

  // Long/Short PnL breakdown
  const longCloseTrades = closeTrades.filter(t => t.action === 'CLOSE_LONG');
  const shortCloseTrades = closeTrades.filter(t => t.action === 'CLOSE_SHORT');

  const longPnl = longCloseTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0);
  const shortPnl = shortCloseTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0);

  const longWinCount = longCloseTrades.filter(t => (t.pnl ?? 0) > 0).length;
  const shortWinCount = shortCloseTrades.filter(t => (t.pnl ?? 0) > 0).length;

  const longWinRate = longCloseTrades.length > 0 ? (longWinCount / longCloseTrades.length) * 100 : 0;
  const shortWinRate = shortCloseTrades.length > 0 ? (shortWinCount / shortCloseTrades.length) * 100 : 0;

  return {
    totalReturn,
    totalReturnPercent,
    maxDrawdown,
    maxDrawdownPercent,
    sharpeRatio,
    sortinoRatio,
    calmarRatio,
    cagrPercent,
    winRate,
    profitFactor,
    totalTrades,
    winningTrades: winCount,
    losingTrades: lossCount,
    avgWin,
    avgLoss,
    avgWinPercent,
    avgLossPercent,
    expectancy,
    expectancyPercent,
    largestWin,
    largestLoss,
    avgTradeDuration,
    exposureTime,
    totalFees,
    totalSlippage,
    slippageUncertaintyBandPercent,
    longPnl,
    shortPnl,
    longTrades: longCloseTrades.length,
    shortTrades: shortCloseTrades.length,
    longWinRate,
    shortWinRate,
    sharpeReturnSignMismatch,
  };
}

/**
 * Calculate average trade duration from open/close trade pairs
 */
function calculateAvgTradeDuration(trades: Trade[]): number {
  // Find matching open/close pairs by closedPositionId
  const closeTrades = trades.filter(t => t.closedPositionId);
  const openTradesById = new Map<string, Trade>();

  for (const trade of trades) {
    if (trade.action === 'OPEN_LONG' || trade.action === 'OPEN_SHORT') {
      openTradesById.set(trade.id, trade);
    }
  }

  let totalDuration = 0;
  let count = 0;

  for (const closeTrade of closeTrades) {
    const openTrade = openTradesById.get(closeTrade.closedPositionId!);
    if (openTrade) {
      totalDuration += closeTrade.timestamp - openTrade.timestamp;
      count++;
    }
  }

  return count > 0 ? totalDuration / count : 0;
}

/**
 * Calculate maximum drawdown from equity curve
 */
export function calculateMaxDrawdown(
  equity: EquityPoint[],
  initialCapital: number
): { maxDrawdown: number; maxDrawdownPercent: number } {
  if (equity.length === 0) {
    return { maxDrawdown: 0, maxDrawdownPercent: 0 };
  }

  let peak = initialCapital;
  let maxDrawdown = 0;
  let maxDrawdownPercent = 0;

  for (const point of equity) {
    if (point.equity > peak) {
      peak = point.equity;
    }

    const drawdown = peak - point.equity;
    const drawdownPercent = (drawdown / peak) * 100;

    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
      maxDrawdownPercent = drawdownPercent;
    }
  }

  return { maxDrawdown, maxDrawdownPercent };
}

/**
 * Calculate period-over-period returns from equity curve.
 *
 * Zero-base handling: when prevEquity ≤ 0, computing a simple return is undefined
 * (division by zero or sign flip). Rather than breaking the loop, we SKIP that one
 * bar and continue — so subsequent bars where prevEquity > 0 are still included.
 *
 * WHY SKIP instead of BREAK:
 * Per-asset equity curves use an isolated-margin display floor of 0. When a sub's
 * notional allocation hits 0 mid-run, the curve is floored there while the portfolio
 * continues trading and the sub can recover in subsequent bars. Breaking at the first
 * zero truncates the return series at the declining prefix, causing Sharpe to see only
 * the losses — while totalReturnPercent reflects the recovered final equity. The result
 * is a sign mismatch (Return > 0, Sharpe < 0). Skipping the one undefined 0→positive
 * transition and including the recovery bars makes Sharpe consistent with the return.
 *
 * PORTFOLIO-RUIN SAFETY:
 * For a truly ruined curve (equity hits 0 and STAYS 0), every subsequent prevEquity
 * is also 0 → every subsequent bar is skipped → behaviour is identical to the old
 * break. No spurious post-ruin returns are included.
 *
 * Common case (equity always > 0): the guard is never triggered; behaviour is identical
 * to before this change (regression-safe).
 */
function calculateReturns(equity: EquityPoint[]): number[] {
  if (equity.length < 2) return [];

  const returns: number[] = [];

  for (let i = 1; i < equity.length; i++) {
    const prevEquity = equity[i - 1].equity;

    // When prevEquity ≤ 0, simple-return math is undefined (division by zero or sign
    // flip). Skip this bar — do NOT break, because subsequent bars where prevEquity > 0
    // (i.e. after a recovery from the 0-floor) must still be included so that Sharpe
    // reflects the same full equity series as totalReturnPercent.
    if (prevEquity <= 0) {
      continue;
    }

    const currEquity = equity[i].equity;
    returns.push((currEquity - prevEquity) / prevEquity);
  }

  return returns;
}

/**
 * Get annualization factor (periods per 365-day year) for a timeframe.
 *
 * Derived from the canonical timeframe table (`timeframeToMs` in core/types.ts),
 * so every member of `ALL_TIMEFRAMES` is covered by construction. Crypto trades
 * 365 days/year, no holidays. The timeframe is REQUIRED: a missing/undefined value throws.
 * An unrecognised timeframe THROWS (no silent fallback).
 *
 * '1w' is pinned to the historical convention of 52 (the formula gives 52.142857...)
 * so existing weekly results do not change.
 */
const YEAR_MS = 365 * 24 * 3600 * 1000;
const ANNUALIZATION_OVERRIDES: Partial<Record<Timeframe, number>> = { '1w': 52 };

export function getAnnualizationFactor(timeframe: string): number {
  const parsed = TimeframeSchema.safeParse(timeframe);
  if (!parsed.success) throw new Error(`Unknown timeframe: ${timeframe}`);
  const tf = parsed.data;
  const override = ANNUALIZATION_OVERRIDES[tf];
  return override !== undefined ? override : YEAR_MS / timeframeToMs(tf);
}

/**
 * Calculate Sharpe Ratio
 * Assumes risk-free rate of 0 and annualizes based on timeframe.
 *
 * @remarks
 * **Calculation basis**: Sharpe is calculated from per-bar returns (equity[i]/equity[i-1] - 1),
 * NOT from per-trade returns. This is standard for crypto (24/7 markets) but means:
 * - A strategy that is in cash most of the time will show diluted Sharpe compared to
 *   a trade-based calculation (which ignores idle periods).
 * - The annualization factor assumes the strategy runs on every bar without gaps.
 * - For a future alternative basis, see the optional `sharpeBasis: 'trades' | 'bars'`
 *   parameter in `calculateMetrics` (not yet implemented — TODO(audit-H8)).
 */
export function calculateSharpeRatio(returns: number[], timeframe: string): number {
  if (returns.length < 2) return 0;

  const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  const variance =
    returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) /
    (returns.length - 1);
  const stdDev = Math.sqrt(variance);

  if (stdDev === 0) return 0;

  // Annualize based on timeframe
  const factor = getAnnualizationFactor(timeframe);
  const annualizedReturn = mean * factor;
  const annualizedStdDev = stdDev * Math.sqrt(factor);

  return annualizedReturn / annualizedStdDev;
}

/**
 * Calculate Sortino Ratio
 * Uses only downside deviation in the denominator
 */
function calculateSortinoRatio(returns: number[], timeframe: string): number {
  if (returns.length < 2) return 0;

  const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;

  // Calculate downside deviation (only negative returns)
  const negativeReturns = returns.filter((r) => r < 0);
  if (negativeReturns.length === 0) {
    // No negative returns means infinite Sortino (or very high)
    return mean > 0 ? SORTINO_CAP : 0;
  }

  const downsideVariance =
    negativeReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) /
    returns.length;
  const downsideDeviation = Math.sqrt(downsideVariance);

  if (downsideDeviation === 0) return 0;

  // Annualize based on timeframe
  const factor = getAnnualizationFactor(timeframe);
  const annualizedReturn = mean * factor;
  const annualizedDownsideDev = downsideDeviation * Math.sqrt(factor);

  return annualizedReturn / annualizedDownsideDev;
}

/**
 * Calculate Compound Annual Growth Rate (CAGR) as a percentage.
 *
 * Formula: (finalEquity / initialEquity)^(1/years) − 1, expressed as %.
 *
 * Guards:
 *   - initialEquity ≤ 0 → 0 (undefined base)
 *   - years ≤ 0 (single equity point or empty series) → 0
 *   - finalEquity ≤ 0 → floor at −100% (cannot lose more than 100%)
 *   - NaN / Infinity → 0
 *
 * @param equity     Full equity-curve array (sorted ascending by timestamp)
 * @param initialCapital  Portfolio starting capital (basis denominator)
 * @returns CAGR expressed as a percentage (e.g. 25.4 means 25.4%/yr)
 */
function calculateCagr(equity: EquityPoint[], initialCapital: number): number {
  const MS_PER_YEAR = 365 * 24 * 3600 * 1000;

  if (initialCapital <= 0) return 0;
  if (equity.length < 2) return 0;

  const firstTs = equity[0].timestamp;
  const lastTs = equity[equity.length - 1].timestamp;
  const years = (lastTs - firstTs) / MS_PER_YEAR;

  if (years <= 0) return 0;

  const finalEquity = equity[equity.length - 1].equity;

  // Ruin: can't lose more than 100%
  if (finalEquity <= 0) return -100;

  const cagr = (Math.pow(finalEquity / initialCapital, 1 / years) - 1) * 100;

  // Guard against NaN / Infinity from exotic edge cases
  if (!isFinite(cagr) || isNaN(cagr)) return 0;

  return cagr;
}

/**
 * Calculate Calmar Ratio
 * CAGR / |maxDrawdownPercent| — canonical industry definition.
 * Returns 0 when maxDrawdown is 0 or CAGR cannot be computed.
 *
 * NOTE: the previous implementation used arithmetic-annualised mean returns
 * (mean × factor × 100) as the numerator. This was non-standard. Calmar is
 * defined as CAGR / maxDD (Schwager 1993, CFA curriculum, QuantConnect).
 */
function calculateCalmarRatio(cagrPercent: number, maxDrawdownPercent: number): number {
  if (maxDrawdownPercent === 0 || cagrPercent === 0) return 0;
  return cagrPercent / maxDrawdownPercent;
}

/**
 * Calculate equity-curve-based risk metrics from an equity series.
 *
 * This is the single source of truth for Sharpe, Sortino, CAGR, Calmar, and
 * max-drawdown used by BOTH the backtest path (`calculateMetrics`) and the
 * paper-trading path (`computePaperMetrics`).  Keeping both callers on this
 * one function guarantees numerical parity.
 *
 * @param equity        Full equity-curve array (sorted ascending by timestamp)
 * @param initialCapital Portfolio starting capital
 * @param timeframe     Required OHLCV timeframe string for annualization (e.g. '1h', '1d')
 */
export function calculateEquityRiskMetrics(
  equity: EquityPoint[],
  initialCapital: number,
  timeframe: string,
): {
  sharpeRatio: number;
  sortinoRatio: number;
  cagrPercent: number;
  calmarRatio: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
} {
  const { maxDrawdown, maxDrawdownPercent } = calculateMaxDrawdown(equity, initialCapital);
  const returns = calculateReturns(equity);
  const sharpeRatio = calculateSharpeRatio(returns, timeframe);
  const sortinoRatio = calculateSortinoRatio(returns, timeframe);
  const cagrPercent = calculateCagr(equity, initialCapital);
  const calmarRatio = calculateCalmarRatio(cagrPercent, maxDrawdownPercent);
  return { sharpeRatio, sortinoRatio, cagrPercent, calmarRatio, maxDrawdown, maxDrawdownPercent };
}

/**
 * Calculate exposure time (percentage of time in the market)
 */
function calculateExposureTime(trades: Trade[], equity: EquityPoint[]): number {
  if (trades.length === 0 || equity.length < 2) return 0;

  const startTime = equity[0].timestamp;
  const endTime = equity[equity.length - 1].timestamp;
  const totalTime = endTime - startTime;

  if (totalTime === 0) return 0;

  // Build a map of open trades by their ID
  const openTradesById = new Map<string, Trade>();
  for (const trade of trades) {
    if (trade.action === 'OPEN_LONG' || trade.action === 'OPEN_SHORT') {
      openTradesById.set(trade.id, trade);
    }
  }

  // Sum up time spent in positions
  let timeInMarket = 0;
  for (const trade of trades) {
    if (trade.closedPositionId) {
      const openTrade = openTradesById.get(trade.closedPositionId);
      if (openTrade) {
        timeInMarket += trade.timestamp - openTrade.timestamp;
      }
    }
  }

  return (timeInMarket / totalTime) * 100;
}

/**
 * Generate equity curve with drawdown calculations
 */
export function generateEquityCurve(
  timestamps: number[],
  equityValues: number[],
  initialCapital: number
): EquityPoint[] {
  if (timestamps.length !== equityValues.length) {
    throw new Error('Timestamps and equity values must have the same length');
  }

  const curve: EquityPoint[] = [];
  let peak = initialCapital;

  for (let i = 0; i < timestamps.length; i++) {
    const equity = equityValues[i];

    if (equity > peak) {
      peak = equity;
    }

    const drawdown = ((peak - equity) / peak) * 100;

    curve.push({
      timestamp: timestamps[i],
      equity,
      drawdown,
    });
  }

  return curve;
}

/**
 * Calculate risk-adjusted metrics
 * @param trades - Array of all trades
 * @param equity - Array of equity points over time
 * @param timeframe - Required timeframe for annualization (e.g., '1h', '1d')
 */
export function calculateRiskMetrics(
  trades: Trade[],
  equity: EquityPoint[],
  // timeframe was used for the old arithmetic-annualized Calmar; now unused (CAGR-based).
  // Kept in signature for backward compatibility with callers that pass it.
  _timeframe?: string
): {
  calmarRatio: number;
  ulcerIndex: number;
  riskRewardRatio: number;
} {
  const { maxDrawdownPercent } = calculateMaxDrawdown(equity, equity[0]?.equity ?? 0);

  // Calmar Ratio — use consolidated CAGR-based implementation (same as calculateMetrics)
  // initialCapital is approximated as the first equity point for calculateRiskMetrics callers.
  const initialEquityForCagr = equity.length > 0 ? equity[0].equity : 0;
  const cagrForCalmar = calculateCagr(equity, initialEquityForCagr);
  const calmarRatio = calculateCalmarRatio(cagrForCalmar, maxDrawdownPercent);

  // Ulcer Index (measures depth and duration of drawdowns)
  const ulcerIndex = calculateUlcerIndex(equity);

  // Risk/Reward Ratio
  const closeTrades = trades.filter(isCloseTrade);
  const winningTrades = closeTrades.filter((t) => (t.pnl ?? 0) > 0);
  const losingTrades = closeTrades.filter((t) => (t.pnl ?? 0) < 0);
  const avgWin =
    winningTrades.length > 0
      ? winningTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0) / winningTrades.length
      : 0;
  const avgLoss =
    losingTrades.length > 0
      ? Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl ?? 0), 0)) / losingTrades.length
      : 0;
  // Risk/reward ratio — cap at PROFIT_FACTOR_CAP to prevent Infinity (JSON.stringify(Infinity) === "null")
  const riskRewardRatio = avgLoss > 0
    ? Math.min(avgWin / avgLoss, PROFIT_FACTOR_CAP)
    : avgWin > 0 ? PROFIT_FACTOR_CAP : 0;

  return {
    calmarRatio,
    ulcerIndex,
    riskRewardRatio,
  };
}

/**
 * Calculate Ulcer Index
 */
function calculateUlcerIndex(equity: EquityPoint[]): number {
  if (equity.length < 2) return 0;

  let sumSquaredDrawdowns = 0;
  let peak = equity[0].equity;

  for (const point of equity) {
    if (point.equity > peak) {
      peak = point.equity;
    }

    const drawdownPercent = ((peak - point.equity) / peak) * 100;
    sumSquaredDrawdowns += drawdownPercent * drawdownPercent;
  }

  return Math.sqrt(sumSquaredDrawdowns / equity.length);
}

/**
 * Calculate rolling performance metrics over time
 * @param trades - Array of all trades
 * @param equity - Array of equity points over time
 * @param initialCapital - Starting capital
 * @param timeframe - Required timeframe for annualization (e.g., '1h', '1d')
 */
export function calculateRollingMetrics(
  trades: Trade[],
  equity: EquityPoint[],
  initialCapital: number,
  timeframe: string
): RollingMetrics {
  const timestamps: number[] = [];
  const cumulativeReturn: number[] = [];
  const drawdown: number[] = [];
  const rollingSharpe: number[] = [];
  const cumulativeWinRate: number[] = [];
  const cumulativeProfitFactor: number[] = [];

  if (equity.length === 0) {
    return { timestamps, cumulativeReturn, drawdown, rollingSharpe, cumulativeWinRate, cumulativeProfitFactor };
  }

  const closeTrades = trades.filter(isCloseTrade);

  // Pre-compute equity returns for rolling Sharpe
  const returns: number[] = [];
  for (let i = 1; i < equity.length; i++) {
    if (equity[i - 1].equity > 0) {
      returns.push((equity[i].equity - equity[i - 1].equity) / equity[i - 1].equity);
    } else {
      returns.push(0);
    }
  }

  // Track cumulative trade stats for win rate and profit factor
  let tradeIdx = 0;
  let wins = 0;
  let totalCloseTrades = 0;
  let grossProfit = 0;
  let grossLoss = 0;

  for (let i = 0; i < equity.length; i++) {
    const point = equity[i];
    timestamps.push(point.timestamp);

    // Cumulative return %
    cumulativeReturn.push(((point.equity - initialCapital) / initialCapital) * 100);

    // Drawdown (already computed in equity)
    drawdown.push(point.drawdown);

    // Rolling Sharpe (ROLLING_SHARPE_WINDOW-bar window of returns)
    if (i < 2) {
      rollingSharpe.push(0);
    } else {
      const windowSize = ROLLING_SHARPE_WINDOW;
      const startIdx = Math.max(0, i - windowSize);
      const windowReturns = returns.slice(startIdx, i);

      if (windowReturns.length < 2) {
        rollingSharpe.push(0);
      } else {
        const mean = windowReturns.reduce((s, r) => s + r, 0) / windowReturns.length;
        const variance = windowReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / (windowReturns.length - 1);
        const std = Math.sqrt(variance);
        const annFactor = getAnnualizationFactor(timeframe);
        rollingSharpe.push(std === 0 ? 0 : (mean / std) * Math.sqrt(annFactor));
      }
    }

    // Advance trade pointer to include trades up to current timestamp
    while (tradeIdx < closeTrades.length && closeTrades[tradeIdx].timestamp <= point.timestamp) {
      const t = closeTrades[tradeIdx];
      totalCloseTrades++;
      const pnl = t.pnl ?? 0;
      if (pnl > 0) {
        wins++;
        grossProfit += pnl;
      } else if (pnl < 0) {
        grossLoss += Math.abs(pnl);
      }
      tradeIdx++;
    }

    // Cumulative win rate
    cumulativeWinRate.push(totalCloseTrades === 0 ? 0 : (wins / totalCloseTrades) * 100);

    // Cumulative profit factor
    if (totalCloseTrades === 0) {
      cumulativeProfitFactor.push(0);
    } else if (grossLoss === 0) {
      cumulativeProfitFactor.push(grossProfit > 0 ? PROFIT_FACTOR_CAP : 0);
    } else {
      cumulativeProfitFactor.push(grossProfit / grossLoss);
    }
  }

  return { timestamps, cumulativeReturn, drawdown, rollingSharpe, cumulativeWinRate, cumulativeProfitFactor };
}
